import { useMemo } from 'react';
import { Avatar, Space, Typography, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface UsePatientOptionRenderOptions {
  showMobile?: boolean;
  showStatus?: boolean;
  size?: 'small' | 'default' | 'large';
}

export const usePatientOptionRender = (
  options: UsePatientOptionRenderOptions = {}
) => {
  const { showMobile = true, showStatus = true, size = 'small' } = options;

  return useMemo(
    () => (patient: any) => {
      // Handle patient data structure with first_name and last_name
      const firstName = patient.first_name || '';
      const lastName = patient.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();

      // Handle mobile number field - use mobile_no from database
      const mobileNumber = patient.mobile_no || '';

      // Handle status field
      const status = patient.status || '';

      // Generate initials from full name
      const getInitials = (name: string) => {
        if (!name) return 'P'; // Default to 'P' for Patient
        return name
          .split(' ')
          .filter(word => word.length > 0) // Filter out empty strings
          .map((word: string) => word.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      };

      const initials = getInitials(fullName);
      const avatarSize =
        size === 'small' ? 'small' : size === 'large' ? 'large' : 'default';

      // Generate consistent color based on full name
      const getAvatarColor = (name: string) => {
        if (!name) return '#52c41a'; // Default green for patients
        const colors = [
          '#52c41a',
          '#1890ff',
          '#722ed1',
          '#eb2f96',
          '#fa8c16',
          '#13c2c2',
          '#f5222d',
          '#faad14',
          '#2f54eb',
          '#a0d911',
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
          hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
      };

      const avatarColor = getAvatarColor(fullName);

      // Return the rendered component - the patient data is already available in the closure
      const renderedOption = (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 8,
          }}
        >
          <Space>
            <Avatar
              size={avatarSize}
              style={{
                backgroundColor: avatarColor,
                color: '#fff',
                fontWeight: 600,
              }}
              icon={!fullName ? <UserOutlined /> : undefined}
            >
              {patient.avatar ? (
                <img
                  src={patient.avatar}
                  alt={fullName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <span
                  style={{
                    fontSize:
                      size === 'small'
                        ? '12px'
                        : size === 'large'
                          ? '16px'
                          : '14px',
                    lineHeight: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {initials}
                </span>
              )}
            </Avatar>
            <div>
              <Text
                style={{
                  fontSize:
                    size === 'small'
                      ? '14px'
                      : size === 'large'
                        ? '16px'
                        : '15px',
                  fontWeight: 500,
                  display: 'block',
                  color: '#262626',
                  maxWidth: '150px',
                }}
                ellipsis={{ tooltip: fullName }}
              >
                {fullName || 'Unknown Patient'}
              </Text>
              {showMobile && mobileNumber && (
                <Text
                  style={{
                    fontSize: '12px',
                    color: '#8c8c8c',
                    display: 'block',
                  }}
                >
                  +{mobileNumber}
                </Text>
              )}
            </div>
          </Space>
          {showStatus && status && (
            <Tag
              color={
                status === 'NEW'
                  ? 'green'
                  : status === 'ACTIVE'
                    ? 'blue'
                    : status === 'INACTIVE'
                      ? 'red'
                      : 'default'
              }
              style={{
                fontSize: '10px',
                fontWeight: 500,
                lineHeight: '1.2',
              }}
            >
              {status}
            </Tag>
          )}
        </div>
      );

      // Return the rendered component - the patient data is already available in the closure
      return renderedOption;
    },
    [showMobile, showStatus, size]
  );
};
